import React from 'react';
import { 
  FileText, BarChart3, DollarSign, TrendingUp, 
  CheckCircle, Clock, AlertTriangle, Sparkles, TrendingDown
} from 'lucide-react';

function MetricCard({ icon: Icon, label, value, iconColor, bgColor, trend, status, subtitle }) {
  // Conditional border and glow based on status
  const borderClass = status === 'excellent' ? 'border-2 border-green-300 shadow-lg shadow-green-100' :
                      status === 'good' ? 'border-2 border-blue-300 shadow-md shadow-blue-50' :
                      status === 'warning' ? 'border-2 border-amber-300 shadow-md shadow-amber-50' :
                      status === 'critical' ? 'border-2 border-red-300 shadow-lg shadow-red-100' : '';
  
  return (
    <div className={`glass-card p-4 relative hover:shadow-xl transition-all duration-300 ${borderClass}`}>
      {trend && (
        <div className={`absolute top-2 right-2 text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
          trend > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
        }`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {Math.abs(trend).toFixed(1)}%
        </div>
      )}
      <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center mb-3 shadow-sm`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-900 mb-1">{value}</p>
      {subtitle && (
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      )}
    </div>
  );
}

export default function AnalyticsKPIs({ summary }) {
  // Add comprehensive safety checks for undefined summary
  const safeSummary = {
    total_scenarios: summary?.total_scenarios || 0,
    total_collections: summary?.total_collections || 0,
    total_gross_revenue: summary?.total_gross_revenue || 0,
    total_net_revenue: summary?.total_net_revenue || 0,
    approval_rate: summary?.approval_rate || 0,
    avg_approval_time_hours: summary?.avg_approval_time_hours || 0
  };
  
  // Calculate intelligent insights using safe values
  const avgDealSize = safeSummary.total_scenarios > 0 ? safeSummary.total_net_revenue / safeSummary.total_scenarios : 0;
  const revenuePerProposal = safeSummary.total_collections > 0 ? safeSummary.total_gross_revenue / safeSummary.total_collections : 0;
  
  // Detect anomalies and trends
  const insights = [];
  
  if (safeSummary.approval_rate < 50 && safeSummary.total_scenarios > 0) {
    insights.push({
      type: 'warning',
      text: `Low approval rate (${safeSummary.approval_rate}%) - Review pricing strategy`
    });
  } else if (safeSummary.approval_rate > 80) {
    insights.push({
      type: 'success',
      text: `Excellent approval rate (${safeSummary.approval_rate}%) - Strong pipeline!`
    });
  }
  
  if (safeSummary.avg_approval_time_hours > 72) {
    insights.push({
      type: 'warning',
      text: `Slow approval time (${safeSummary.avg_approval_time_hours.toFixed(1)}h) - Optimize workflow`
    });
  }
  
  if (avgDealSize > 15000) {
    insights.push({
      type: 'success',
      text: `High-value deals averaging $${(avgDealSize/1000).toFixed(1)}K - Premium positioning`
    });
  }
  
  if (safeSummary.total_scenarios > 50 && safeSummary.approval_rate > 60) {
    insights.push({
      type: 'info',
      text: `Strong pipeline: ${safeSummary.total_scenarios} scenarios with ${safeSummary.approval_rate}% win rate`
    });
  }

  return (
    <>
      {/* Intelligent Insights Banner */}
      {insights.length > 0 && (
        <div className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold text-indigo-900 mb-2">💡 AI-Powered Insights</h3>
              <div className="space-y-2">
                {insights.map((insight, idx) => (
                  <div key={idx} className={`flex items-start gap-2 text-sm ${
                    insight.type === 'warning' ? 'text-amber-800' :
                    insight.type === 'success' ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    {insight.type === 'warning' && <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'success' && <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    {insight.type === 'info' && <TrendingUp className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                    <p>{insight.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics with Conditional Formatting */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <MetricCard
          icon={FileText}
          label="Total Scenarios"
          value={safeSummary.total_scenarios}
          iconColor="text-blue-600"
          bgColor="bg-gradient-to-br from-blue-50 to-blue-100"
          status={safeSummary.total_scenarios > 30 ? 'excellent' : safeSummary.total_scenarios > 15 ? 'good' : 'warning'}
          subtitle={safeSummary.total_scenarios > 30 ? 'Strong pipeline' : 'Growing'}
        />
        <MetricCard
          icon={BarChart3}
          label="Total Proposals"
          value={safeSummary.total_collections}
          iconColor="text-purple-600"
          bgColor="bg-gradient-to-br from-purple-50 to-purple-100"
          status={safeSummary.total_collections > 10 ? 'excellent' : 'good'}
          subtitle={`${safeSummary.total_scenarios} scenarios`}
        />
        <MetricCard
          icon={DollarSign}
          label="Gross Revenue"
          value={`$${(safeSummary.total_gross_revenue / 1000).toFixed(1)}K`}
          iconColor="text-green-600"
          bgColor="bg-gradient-to-br from-green-50 to-green-100"
          status={safeSummary.total_gross_revenue > 50000 ? 'excellent' : safeSummary.total_gross_revenue > 20000 ? 'good' : 'warning'}
          subtitle="Annual projection"
        />
        <MetricCard
          icon={TrendingUp}
          label="Net Revenue"
          value={`$${(safeSummary.total_net_revenue / 1000).toFixed(1)}K`}
          iconColor="text-emerald-600"
          bgColor="bg-gradient-to-br from-emerald-50 to-emerald-100"
          status={safeSummary.total_net_revenue > 10000 ? 'excellent' : safeSummary.total_net_revenue > 5000 ? 'good' : 'warning'}
          subtitle={`${((safeSummary.total_net_revenue / safeSummary.total_gross_revenue) * 100 || 0).toFixed(1)}% margin`}
        />
        <MetricCard
          icon={CheckCircle}
          label="Approval Rate"
          value={`${safeSummary.approval_rate.toFixed(1)}%`}
          iconColor={safeSummary.approval_rate > 75 ? 'text-green-600' : safeSummary.approval_rate > 50 ? 'text-blue-600' : 'text-amber-600'}
          bgColor={safeSummary.approval_rate > 75 ? 'bg-gradient-to-br from-green-50 to-green-100' : safeSummary.approval_rate > 50 ? 'bg-gradient-to-br from-blue-50 to-blue-100' : 'bg-gradient-to-br from-amber-50 to-amber-100'}
          status={safeSummary.approval_rate > 75 ? 'excellent' : safeSummary.approval_rate > 50 ? 'good' : 'warning'}
          subtitle={safeSummary.approval_rate > 75 ? 'Excellent!' : safeSummary.approval_rate > 50 ? 'On track' : 'Needs review'}
        />
        <MetricCard
          icon={Clock}
          label="Avg Approval Time"
          value={`${safeSummary.avg_approval_time_hours.toFixed(1)}h`}
          iconColor={safeSummary.avg_approval_time_hours < 24 ? 'text-green-600' : safeSummary.avg_approval_time_hours < 48 ? 'text-blue-600' : 'text-amber-600'}
          bgColor={safeSummary.avg_approval_time_hours < 24 ? 'bg-gradient-to-br from-green-50 to-green-100' : safeSummary.avg_approval_time_hours < 48 ? 'bg-gradient-to-br from-blue-50 to-blue-100' : 'bg-gradient-to-br from-amber-50 to-amber-100'}
          status={safeSummary.avg_approval_time_hours < 24 ? 'excellent' : safeSummary.avg_approval_time_hours < 48 ? 'good' : 'warning'}
          subtitle={safeSummary.avg_approval_time_hours < 24 ? 'Fast!' : safeSummary.avg_approval_time_hours < 48 ? 'Good pace' : 'Can improve'}
        />
      </div>

      {/* Revenue-Specific Metrics with Enhanced Visuals */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className={`glass-card p-5 hover:shadow-xl transition-all duration-300 ${
          avgDealSize > 15000 ? 'border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white' : ''
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl flex items-center justify-center shadow-sm">
              <DollarSign className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Avg Deal Size</p>
              <p className="text-2xl font-bold text-slate-900">${(avgDealSize/1000).toFixed(1)}K</p>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full transition-all duration-700"
              style={{ width: `${Math.min((avgDealSize / 20000) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {avgDealSize > 15000 ? '🎯 Premium deals' : avgDealSize > 8000 ? '✓ Good average' : 'Growing'}
          </p>
        </div>
        
        <div className="glass-card p-5 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Revenue/Proposal</p>
              <p className="text-2xl font-bold text-slate-900">${(revenuePerProposal/1000).toFixed(1)}K</p>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-400 to-purple-600 rounded-full transition-all duration-700"
              style={{ width: `${Math.min((revenuePerProposal / 30000) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Per proposal collection
          </p>
        </div>
        
        <div className={`glass-card p-5 hover:shadow-xl transition-all duration-300 ${
          safeSummary.approval_rate > 75 ? 'border-2 border-green-200 bg-gradient-to-br from-green-50/50 to-white' : ''
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-gradient-to-br from-green-50 to-green-100 rounded-xl flex items-center justify-center shadow-sm">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Win Rate</p>
              <p className="text-2xl font-bold text-slate-900">{safeSummary.approval_rate.toFixed(1)}%</p>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-700 ${
                safeSummary.approval_rate > 75 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                safeSummary.approval_rate > 50 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                'bg-gradient-to-r from-amber-400 to-amber-600'
              }`}
              style={{ width: `${safeSummary.approval_rate}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {safeSummary.approval_rate > 75 ? '🏆 Excellent conversion' : safeSummary.approval_rate > 50 ? '✓ Good performance' : '⚠ Needs improvement'}
          </p>
        </div>
        
        <div className={`glass-card p-5 hover:shadow-xl transition-all duration-300 ${
          safeSummary.avg_approval_time_hours < 24 ? 'border-2 border-blue-200 bg-gradient-to-br from-blue-50/50 to-white' : ''
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl flex items-center justify-center shadow-sm">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Cycle Time</p>
              <p className="text-2xl font-bold text-slate-900">{safeSummary.avg_approval_time_hours.toFixed(1)}h</p>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-700 ${
                safeSummary.avg_approval_time_hours < 24 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                safeSummary.avg_approval_time_hours < 48 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                'bg-gradient-to-r from-amber-400 to-amber-600'
              }`}
              style={{ width: `${Math.max(100 - (safeSummary.avg_approval_time_hours / 72 * 100), 20)}%` }}
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {safeSummary.avg_approval_time_hours < 24 ? '⚡ Lightning fast' : safeSummary.avg_approval_time_hours < 48 ? '✓ Good velocity' : '🐌 Can optimize'}
          </p>
        </div>
      </div>

      
      {/* Performance Indicators Summary */}
      <div className="glass-card p-5 bg-gradient-to-r from-slate-50 to-slate-100 border-l-4 border-indigo-500">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-slate-900 mb-2">📊 Performance Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500 mb-1">Pipeline Health</p>
                <p className={`font-bold ${safeSummary.total_scenarios > 30 ? 'text-green-600' : safeSummary.total_scenarios > 15 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {safeSummary.total_scenarios > 30 ? '✓ Strong' : safeSummary.total_scenarios > 15 ? '↗ Growing' : '⚡ Building'}
                </p>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Revenue Velocity</p>
                <p className={`font-bold ${safeSummary.total_gross_revenue > 50000 ? 'text-green-600' : safeSummary.total_gross_revenue > 20000 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {safeSummary.total_gross_revenue > 50000 ? '🚀 High' : safeSummary.total_gross_revenue > 20000 ? '✓ Good' : '📈 Moderate'}
                </p>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Conversion Rate</p>
                <p className={`font-bold ${safeSummary.approval_rate > 75 ? 'text-green-600' : safeSummary.approval_rate > 50 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {safeSummary.approval_rate > 75 ? '🏆 Excellent' : safeSummary.approval_rate > 50 ? '✓ Good' : '⚠ Review'}
                </p>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Process Efficiency</p>
                <p className={`font-bold ${safeSummary.avg_approval_time_hours < 24 ? 'text-green-600' : safeSummary.avg_approval_time_hours < 48 ? 'text-blue-600' : 'text-amber-600'}`}>
                  {safeSummary.avg_approval_time_hours < 24 ? '⚡ Fast' : safeSummary.avg_approval_time_hours < 48 ? '✓ Good' : '🔄 Optimize'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
